import type {ConsolidatedReviewState} from '@github-ui/mergebox/sections/ReviewerSection'
import {ArrowUpRightIcon} from '@primer/octicons-react'
import {MiniMergeStatusItem} from './MiniMergeStatusItem'
import type {MiniStatusIconProps} from './MiniStatusIcon'
import {MiniMergeStatusBoundary} from './MiniMergeStatusBoundary'
import {useGoToMergeBox} from '../../../mergebox/use-go-to-mergebox'
import {useMergeabilityData, type MergeabilityData} from '../use-mergeability-data'

type ReviewerSection = Pick<
  MergeabilityData['reviewerSection'],
  | 'numReviewsRequired' // the number of approvals required to merge the pull request from rules
  | 'codeownersRequired'
  | 'latestOpinionatedReviews'
  | 'pendingRequestedReviews'
  | 'sectionStatus'
>

export function ApprovalsMiniStatus() {
  const data = useMergeabilityData()
  const goToMergeBox = useGoToMergeBox()

  const reviewerSection = data?.reviewerSection
  const {successful, total} = reviewerSection ? getApprovalsRatio(reviewerSection) : {successful: 0, total: 0}
  const isUnavailable = reviewerSection ? !hasReviewerContent(reviewerSection) : false
  const iconType = reviewerSection ? iconTypeByReviewsState[reviewerSection.sectionStatus] : 'error'
  const status = reviewerSection ? reviewStatusText[reviewerSection.sectionStatus] : 'Approvals unavailable'
  const progressLabel = reviewerSection
    ? reviewerSection.numReviewsRequired > 0 ||
      reviewerSection.codeownersRequired ||
      reviewerSection.sectionStatus === 'REVIEW_REQUIRED'
      ? `${successful} of ${total} required approvals received`
      : `${successful} of ${total} reviews approved`
    : '0 of 0 approvals'

  return (
    <MiniMergeStatusItem
      aria-label={isUnavailable ? 'Approvals' : 'View approvals'}
      // TODO: Update the panel to open the approvals section in the merge status panel. The current open panel options
      // don't support expanding sections within the merge status panel
      onClick={isUnavailable ? undefined : event => goToMergeBox(event.currentTarget)}
    >
      <MiniMergeStatusItem.LabelRow icon={isUnavailable ? undefined : ArrowUpRightIcon}>
        <MiniMergeStatusItem.Label
          icon={iconType}
          isUnavailable={isUnavailable}
          status={status}
          successful={successful}
          total={total}
        >
          Approvals
        </MiniMergeStatusItem.Label>
      </MiniMergeStatusItem.LabelRow>
      <MiniMergeStatusItem.Progress
        aria-label={progressLabel}
        isUnavailable={isUnavailable}
        successful={successful}
        total={total}
        unavailableLabel="No approvals required"
      />
    </MiniMergeStatusItem>
  )
}

export function ApprovalsMiniStatusBoundary({children}: {children: React.ReactNode}) {
  const goToMergeBox = useGoToMergeBox()

  return (
    <MiniMergeStatusBoundary
      action={goToMergeBox}
      actionLabel="View approvals"
      boundaryName="ApprovalsMiniStatusBoundary"
      text="Approvals"
    >
      {children}
    </MiniMergeStatusBoundary>
  )
}

function hasReviewerContent({
  numReviewsRequired,
  codeownersRequired,
  latestOpinionatedReviews,
  pendingRequestedReviews,
  sectionStatus,
}: ReviewerSection) {
  return (
    numReviewsRequired > 0 ||
    codeownersRequired ||
    latestOpinionatedReviews.length > 0 ||
    pendingRequestedReviews.length > 0 ||
    sectionStatus !== 'REVIEWED'
  )
}

function getApprovalsRatio({
  numReviewsRequired,
  codeownersRequired,
  latestOpinionatedReviews,
  pendingRequestedReviews,
  sectionStatus,
}: ReviewerSection) {
  const approvedReviews = latestOpinionatedReviews.filter(review => review.state === 'APPROVED')
  const approvedReviewCount = approvedReviews.filter(review => review.authorCanPushToRepository).length

  if (codeownersRequired) {
    // Manually build codeowner approval ratio because the rule doesn't expose the number of required codeowners - only the fulfillment.
    // The rule doesn't expose how many have been fulfilled or how many are pending, so we have to count the pending codeowners
    // and add them to the approved count to get a total. This can inflate the total count if an approval is received
    // from a non-codeowner, but this is the best we can do with the data we have.
    const pendingCodeownerCount = pendingRequestedReviews.filter(request => request.isCodeOwner).length
    return {
      successful: approvedReviewCount,
      // Take the most restrictive count between the two rules so that we don't undercount the total required approvals.
      total: Math.max(numReviewsRequired, approvedReviewCount + pendingCodeownerCount, 1),
    }
  }

  if (numReviewsRequired > 0) {
    return {successful: approvedReviewCount, total: numReviewsRequired}
  }

  const reviewActivityCount = latestOpinionatedReviews.length + pendingRequestedReviews.length
  const activeStateMinimum = sectionStatus === 'REVIEWED' ? 0 : 1

  return {successful: approvedReviewCount, total: Math.max(reviewActivityCount, activeStateMinimum)}
}

const iconTypeByReviewsState: Record<ConsolidatedReviewState, MiniStatusIconProps['type']> = {
  APPROVED: 'success',
  CHANGES_REQUESTED: 'failure',
  REVIEW_REQUIRED: 'warning',
  REVIEWED: 'neutral',
  REVIEW_REQUESTED: 'warning',
}

const reviewStatusText: Record<ConsolidatedReviewState, string> = {
  APPROVED: 'Approved',
  CHANGES_REQUESTED: 'Changes requested',
  REVIEW_REQUIRED: 'Review required',
  REVIEWED: 'Reviewed',
  REVIEW_REQUESTED: 'Review requested',
}
